import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useBreadcrumbTitle } from '@/lib/breadcrumbStore';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Euro, MapPin, Calendar, Wrench, Cog, MessageSquare, Send, Clock, Eye, Users, Truck, Calculator } from 'lucide-react';
import { getListing, createEnquiry, publishListing, archiveListing, markListingSold, getSites } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const CONDITION_LABEL = {
  new: 'Neu', refurbished: 'Generalüberholt',
  used_excellent: 'Gebraucht – sehr gut', used_good: 'Gebraucht – gut',
  used_fair: 'Akzeptabel', for_parts: 'Für Teile',
};

const INCOTERMS = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DDP'];

const DEFAULT_ENQ_FORM = {
  enquiry_type: 'general',
  message: '',
  preferred_date: '',
  contact_phone: '',
  requested_qty: '',
  bid_price_EUR: '',
  incoterm: '',
  delivery_site_id: '',
};

export default function ListingDetailPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  useBreadcrumbTitle(listing?.title);
  const [loading, setLoading] = useState(true);
  const [enqOpen, setEnqOpen] = useState(false);
  const [enqForm, setEnqForm] = useState(DEFAULT_ENQ_FORM);
  const [sites, setSites] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getListing(listingId);
      setListing(res.data);
    } catch (e) {
      toast.error('Inserat nicht gefunden');
      navigate('/listings');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [listingId]);

  // Load sites + prefill the enquiry form when the dialog opens (parity with material requests)
  useEffect(() => {
    if (!enqOpen || !listing) return;
    const loadSites = async () => {
      if (!user) { setSites([]); return; }
      try {
        const res = await getSites();
        const all = res.data || [];
        const role = user?.role;
        const userSiteId = user?.site_id;
        const userGroupId = user?.group_id;
        let available = [];
        if (role === 'group_admin') {
          available = userGroupId ? all.filter(s => s.group_id === userGroupId) : [];
        } else if (role === 'admin') {
          available = userGroupId ? all.filter(s => s.group_id === userGroupId) : all;
        } else if (userSiteId) {
          available = all.filter(s => s.site_id === userSiteId);
        } else if (userGroupId) {
          available = all.filter(s => s.group_id === userGroupId);
        }
        setSites(available);
      } catch {
        setSites([]);
      }
    };
    loadSites();
    setEnqForm(prev => ({
      ...DEFAULT_ENQ_FORM,
      ...prev,
      bid_price_EUR: prev.bid_price_EUR || (listing.price_eur != null ? String(listing.price_eur) : ''),
      requested_qty: prev.requested_qty || (listing.vertical === 'machines' ? '1' : (listing.quantity ? String(listing.quantity) : '')),
      delivery_site_id: prev.delivery_site_id || (user?.site_id || ''),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqOpen, listing, user]);

  if (loading || !listing) return <div className="min-h-screen bg-background"><Header /><div className="p-12 text-center text-muted-foreground">Laden…</div></div>;

  const isOwner = user && user.user_id === listing.seller_user_id;
  const isAdmin = user && user.role === 'admin';
  const canEdit = isOwner || isAdmin;
  const isMachines = listing.vertical === 'machines';

  const submitEnquiry = async () => {
    if (!user) {
      navigate('/login?next=/listing/' + listingId);
      return;
    }
    if (!enqForm.message.trim()) { toast.error('Nachricht ist Pflicht'); return; }
    const qty = enqForm.requested_qty ? parseFloat(enqForm.requested_qty) : null;
    if (qty !== null && (!(qty > 0))) { toast.error('Ungültige Menge'); return; }
    if (listing.vertical === 'spare_parts' && listing.quantity != null && qty && qty > listing.quantity) {
      toast.error('Angefragte Menge überschreitet den verfügbaren Bestand');
      return;
    }
    try {
      await createEnquiry({
        listing_id: listingId,
        enquiry_type: enqForm.enquiry_type,
        message: enqForm.message,
        preferred_date: enqForm.preferred_date || null,
        contact_phone: enqForm.contact_phone || null,
        requested_qty: qty,
        bid_price_EUR: enqForm.bid_price_EUR ? parseFloat(enqForm.bid_price_EUR) : null,
        incoterm: enqForm.incoterm || null,
        delivery_site_id: enqForm.delivery_site_id || null,
      });
      toast.success('Anfrage gesendet');
      setEnqOpen(false);
      setEnqForm(DEFAULT_ENQ_FORM);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  const handlePublish = async () => {
    try { await publishListing(listingId); toast.success('Veröffentlicht'); load(); } catch (e) { toast.error('Fehler'); }
  };
  const handleArchive = async () => {
    try { await archiveListing(listingId); toast.success('Archiviert'); load(); } catch (e) { toast.error('Fehler'); }
  };
  const handleSold = async () => {
    try { await markListingSold(listingId); toast.success('Als verkauft markiert'); load(); } catch (e) { toast.error('Fehler'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-5xl" data-testid="listing-detail-page">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/listings?vertical=' + listing.vertical)} data-testid="back-to-listings">
            <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
          </Button>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(`/listing/${listingId}/edit`)} data-testid="edit-listing-btn">
                <Edit className="w-4 h-4 mr-2" /> Bearbeiten
              </Button>
              {listing.status === 'draft' && (
                <Button onClick={handlePublish} data-testid="publish-btn-detail"><Send className="w-4 h-4 mr-2" />Veröffentlichen</Button>
              )}
              {listing.status === 'active' && (
                <>
                  <Button variant="outline" onClick={handleSold} data-testid="mark-sold-btn">Verkauft markieren</Button>
                  <Button variant="outline" onClick={handleArchive} data-testid="archive-btn">Archivieren</Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="aspect-video bg-muted overflow-hidden">
                {(listing.photos || []).length > 0 ? (
                  <img src={listing.photos[0]} alt={listing.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {isMachines ? <Cog className="w-20 h-20 text-muted-foreground/30" /> : <Wrench className="w-20 h-20 text-muted-foreground/30" />}
                  </div>
                )}
              </div>
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" data-testid="listing-vertical-badge">{isMachines ? 'Druckmaschine' : 'Ersatzteil'}</Badge>
                  {listing.condition && <Badge variant="secondary">{CONDITION_LABEL[listing.condition] || listing.condition}</Badge>}
                  {listing.status !== 'active' && <Badge variant="destructive">{listing.status}</Badge>}
                  {listing.visibility === 'group_only' && (
                    <Badge variant="outline" className="gap-1 border-orange-300 text-orange-700 bg-orange-50" data-testid="listing-group-only-badge">
                      <Users className="w-3 h-3" /> Nur in Gruppe {listing.seller_group_name ? `"${listing.seller_group_name}"` : ''}
                    </Badge>
                  )}
                </div>
                <h1 className="text-3xl font-bold tracking-tight" data-testid="listing-title">{listing.title}</h1>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  {listing.manufacturer_name && <span className="font-medium">{listing.manufacturer_name}</span>}
                  {listing.model_name && <span>{listing.model_name}</span>}
                  {listing.year_of_manufacture && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{listing.year_of_manufacture}</span>}
                  {listing.country && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{listing.country}</span>}
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{listing.view_count || 0}</span>
                </div>
                {listing.description && (
                  <>
                    <Separator />
                    <p className="whitespace-pre-wrap text-sm" data-testid="listing-description">{listing.description}</p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Specs */}
            {isMachines && listing.specs && (
              <Card>
                <CardHeader><CardTitle className="text-base">Technische Daten</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {listing.specs.max_width_mm && <Spec label="Max. Breite" value={`${listing.specs.max_width_mm} mm`} />}
                  {listing.specs.num_colors && <Spec label="Farben" value={listing.specs.num_colors} />}
                  {listing.specs.max_speed_mh && <Spec label="Max. Geschwindigkeit" value={`${listing.specs.max_speed_mh} m/h`} />}
                  {listing.specs.power_kw && <Spec label="Leistung" value={`${listing.specs.power_kw} kW`} />}
                  {listing.specs.weight_kg && <Spec label="Gewicht" value={`${listing.specs.weight_kg} kg`} />}
                  {listing.specs.dimensions_lwh_mm && <Spec label="Abmessungen" value={listing.specs.dimensions_lwh_mm} />}
                  {listing.operating_hours && <Spec label="Betriebsstunden" value={listing.operating_hours} />}
                  {listing.inspection_available && <Spec label="Besichtigung" value="Möglich" />}
                </CardContent>
              </Card>
            )}

            {listing.vertical === 'paints' && (
              <Card data-testid="paints-detail-card">
                <CardHeader>
                  <CardTitle className="text-base">Farbe / Lack / Additiv – Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {listing.manufacturer_name && <Spec label="Hersteller" value={listing.manufacturer_name} />}
                    {listing.part_number && <Spec label="Artikelnr." value={listing.part_number} />}
                    {listing.oem_number && <Spec label="Farbcode / Pantone" value={listing.oem_number} />}
                    {listing.serial_number && <Spec label="Charge / Lot" value={listing.serial_number} />}
                    {listing.quantity != null && <Spec label="Menge" value={`${listing.quantity} ${listing.unit || ''}`} />}
                  </div>

                  <Separator />
                  <div>
                    <p className="font-semibold text-fuchsia-700 dark:text-fuchsia-400 mb-2">REACH &amp; CLP – Sicherheitsangaben</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {listing.best_before_date && (
                        <Spec
                          label="MHD"
                          value={new Date(listing.best_before_date).toLocaleDateString('de-DE')}
                        />
                      )}
                      {listing.ufi_code && <Spec label="UFI-Code" value={listing.ufi_code} />}
                      {listing.signal_word && listing.signal_word !== 'none' && (
                        <Spec
                          label="Signalwort"
                          value={listing.signal_word === 'danger' ? 'Gefahr' : 'Achtung'}
                        />
                      )}
                    </div>
                    {listing.hazard_statements && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground">H-Sätze</p>
                        <p data-testid="paints-hazard-statements">{listing.hazard_statements}</p>
                      </div>
                    )}
                    {(listing.hazard_pictograms || []).length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-1">GHS-Piktogramme</p>
                        <div className="flex flex-wrap gap-1.5" data-testid="paints-pictograms">
                          {listing.hazard_pictograms.map(p => (
                            <Badge key={p} variant="outline" className="border-fuchsia-300 text-fuchsia-700">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {listing.safety_data_sheet_url && (
                      <div className="mt-3">
                        <a
                          href={listing.safety_data_sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-fuchsia-50 dark:bg-fuchsia-950/40 text-fuchsia-700 dark:text-fuchsia-300 text-sm font-medium hover:bg-fuchsia-100 transition-colors"
                          data-testid="paints-sdb-link"
                        >
                          📄 Sicherheitsdatenblatt (SDB) – {listing.safety_data_sheet_name || 'PDF'}
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {listing.vertical === 'spare_parts' && (
              <Card>
                <CardHeader><CardTitle className="text-base">Teil-Details</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {listing.part_number && <Spec label="Teile-Nr." value={listing.part_number} />}
                    {listing.oem_number && <Spec label="OEM-Nr." value={listing.oem_number} />}
                    {listing.quantity != null && <Spec label="Menge" value={`${listing.quantity} ${listing.unit || ''}`} />}
                  </div>
                  {(listing.compatible_machines || []).length > 0 && (
                    <div>
                      <p className="font-medium mb-1">Kompatible Maschinen</p>
                      <div className="flex flex-wrap gap-2">
                        {listing.compatible_machines.map((cm, i) => (
                          <Badge key={i} variant="outline" data-testid={`compat-tag-${i}`}>
                            {cm.manufacturer_name || '—'}{cm.model_name ? ` ${cm.model_name}` : ''}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {(listing.video_links || []).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Videos</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {listing.video_links.map((v, i) => (
                    <a key={i} href={v} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline block text-sm" data-testid={`video-link-${i}`}>{v}</a>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-3xl font-bold text-orange-600 mb-3" data-testid="listing-price">
                  <Euro className="w-7 h-7" />
                  {listing.price_eur != null ? Number(listing.price_eur).toLocaleString('de-DE') : 'Auf Anfrage'}
                </div>
                {listing.negotiable && <Badge variant="secondary" className="mb-3">Verhandelbar</Badge>}

                <Separator className="my-3" />
                <div className="space-y-1 text-sm">
                  <p className="text-muted-foreground">Verkäufer</p>
                  <p className="font-medium" data-testid="listing-seller">{listing.seller_group_name || '—'}</p>
                  {(listing.city || listing.country) && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />{[listing.city, listing.country].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>

                {!isOwner && (
                  <Dialog open={enqOpen} onOpenChange={setEnqOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full mt-4" size="lg" data-testid="send-enquiry-btn">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        {isMachines ? 'Anfrage senden' : 'Verkäufer kontaktieren'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg" data-testid="enquiry-dialog">
                      <DialogHeader>
                        <DialogTitle>Anfrage zum Inserat</DialogTitle>
                        <DialogDescription>{listing.title}</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto pr-1">
                        <div>
                          <Label>Anfragetyp</Label>
                          <Select value={enqForm.enquiry_type} onValueChange={v => setEnqForm({ ...enqForm, enquiry_type: v })}>
                            <SelectTrigger data-testid="enquiry-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">Allgemeine Anfrage</SelectItem>
                              {isMachines && <SelectItem value="inspection">Besichtigungstermin</SelectItem>}
                              <SelectItem value="documents">Dokumente anfragen</SelectItem>
                              <SelectItem value="photos">Weitere Fotos anfragen</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {enqForm.enquiry_type === 'inspection' && (
                          <div>
                            <Label>Wunschtermin</Label>
                            <Input type="date" value={enqForm.preferred_date} onChange={e => setEnqForm({ ...enqForm, preferred_date: e.target.value })} data-testid="enquiry-date" />
                          </div>
                        )}

                        {/* Quantity */}
                        <div>
                          <Label>Menge{listing.vertical === 'spare_parts' && listing.unit ? ` (${listing.unit})` : ''}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            max={listing.vertical === 'spare_parts' ? (listing.quantity || undefined) : undefined}
                            value={enqForm.requested_qty}
                            onChange={e => setEnqForm({ ...enqForm, requested_qty: e.target.value })}
                            placeholder={isMachines ? '1' : 'Menge'}
                            data-testid="enquiry-quantity"
                          />
                          {listing.vertical === 'spare_parts' && listing.quantity != null && (
                            <p className="text-xs text-muted-foreground mt-1">Verfügbar: {listing.quantity} {listing.unit || ''}</p>
                          )}
                        </div>

                        {/* Bid Price */}
                        <div>
                          <Label className="flex items-center gap-1">
                            <Euro className="w-3 h-3 text-orange-500" />
                            Gebotspreis (EUR)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={enqForm.bid_price_EUR}
                            onChange={e => setEnqForm({ ...enqForm, bid_price_EUR: e.target.value })}
                            placeholder={listing.price_eur != null ? String(listing.price_eur) : 'Preis'}
                            data-testid="enquiry-bid-price"
                          />
                          {listing.price_eur != null && (
                            <p className="text-xs text-muted-foreground mt-1">Listing-Preis: {Number(listing.price_eur).toLocaleString('de-DE')} EUR{listing.negotiable ? ' (verhandelbar)' : ''}</p>
                          )}
                        </div>

                        {/* Price per unit (live) */}
                        {enqForm.bid_price_EUR && enqForm.requested_qty && parseFloat(enqForm.requested_qty) > 0 && (
                          <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded flex items-center gap-2" data-testid="enquiry-price-per-unit">
                            <Calculator className="w-3 h-3 text-orange-500" />
                            <span className="text-xs font-medium">
                              Preis je Einheit: {(parseFloat(enqForm.bid_price_EUR) / parseFloat(enqForm.requested_qty)).toFixed(2)} EUR{listing.vertical === 'spare_parts' && listing.unit ? `/${listing.unit}` : ''}
                            </span>
                          </div>
                        )}

                        <Separator />

                        {/* Incoterm */}
                        <div>
                          <Label className="flex items-center gap-1"><Truck className="w-3 h-3" />Incoterm</Label>
                          <Select value={enqForm.incoterm} onValueChange={v => setEnqForm({ ...enqForm, incoterm: v })}>
                            <SelectTrigger data-testid="enquiry-incoterm"><SelectValue placeholder="Incoterm auswählen" /></SelectTrigger>
                            <SelectContent>
                              {INCOTERMS.map(ic => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Delivery Site */}
                        <div>
                          <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" />Lieferstandort</Label>
                          <Select value={enqForm.delivery_site_id} onValueChange={v => setEnqForm({ ...enqForm, delivery_site_id: v })}>
                            <SelectTrigger data-testid="enquiry-delivery-site"><SelectValue placeholder="Lieferstandort auswählen" /></SelectTrigger>
                            <SelectContent>
                              {sites.map(s => (
                                <SelectItem key={s.site_id} value={s.site_id}>
                                  {s.site_name} ({s.city})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Nachricht *</Label>
                          <Textarea rows={4} value={enqForm.message} onChange={e => setEnqForm({ ...enqForm, message: e.target.value })} data-testid="enquiry-message" />
                        </div>
                        <div>
                          <Label>Telefon (optional)</Label>
                          <Input value={enqForm.contact_phone} onChange={e => setEnqForm({ ...enqForm, contact_phone: e.target.value })} data-testid="enquiry-phone" />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setEnqOpen(false)}>Abbrechen</Button>
                        <Button onClick={submitEnquiry} data-testid="enquiry-submit"><Send className="w-4 h-4 mr-2" />Senden</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}

                {listing.expires_at && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-4">
                    <Clock className="w-3 h-3" />
                    Läuft ab: {new Date(listing.expires_at).toLocaleDateString('de-DE')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
